import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import api from '@/api/axios';

const CommonCodeContext = createContext(null);

export const CommonCodeProvider = ({ children }) => {
  // 화면 렌더링용 캐시 (getCachedSubCodes / codes 로 노출)
  const [codes, setCodes] = useState({});

  // 실제 캐시와 진행 중 요청은 ref 로 둔다.
  // state 에 의존하면 getSubCodes 의 신원이 매번 바뀌어,
  // 이를 useEffect 의존성에 넣은 화면들이 반복 호출된다.
  const cacheRef = useRef({});
  const inflightRef = useRef({});

  /**
   * 공통코드 그룹의 하위 코드 목록을 가져온다.
   *
   * 같은 그룹을 여러 화면이 동시에 요청해도 요청은 한 번만 나가고
   * **모두 같은 결과를 받는다.**
   * (이전 구현은 "이미 로딩 중"이면 빈 배열을 즉시 돌려주고 재시도하지 않아,
   *  두 번째 화면의 드롭다운이 영구히 비어 보이는 문제가 있었다)
   */
  const getSubCodes = useCallback(async (comCode) => {
    if (!comCode) return [];

    // 1) 이미 받아둔 값
    if (cacheRef.current[comCode]) return cacheRef.current[comCode];

    // 2) 같은 요청이 진행 중이면 그 약속을 함께 기다린다
    if (inflightRef.current[comCode]) return inflightRef.current[comCode];

    // 3) 새로 요청
    const request = (async () => {
      try {
        const response = await api.get(`/commoncodes/sub/${comCode}`);
        const data = Array.isArray(response.data) ? response.data : [];

        cacheRef.current[comCode] = data;
        setCodes((prev) => ({ ...prev, [comCode]: data }));
        return data;
      } catch (error) {
        console.error(`Failed to fetch common codes for ${comCode}:`, error);
        // 실패는 캐시하지 않는다 — 다음 호출에서 다시 시도할 수 있어야 한다.
        return [];
      } finally {
        delete inflightRef.current[comCode];
      }
    })();

    inflightRef.current[comCode] = request;
    return request;
  }, []);

  /** 캐시를 비우고 다시 받아온다(공통코드 관리 화면에서 수정한 뒤 사용) */
  const refreshSubCodes = useCallback(async (comCode) => {
    delete cacheRef.current[comCode];
    delete inflightRef.current[comCode];
    setCodes((prev) => {
      const next = { ...prev };
      delete next[comCode];
      return next;
    });
    return getSubCodes(comCode);
  }, [getSubCodes]);

  /** 동기적으로 현재 캐시된 값을 읽는다 (렌더링용) */
  const getCachedSubCodes = useCallback((comCode) => codes[comCode] || [], [codes]);

  const value = {
    codes,
    getSubCodes,
    refreshSubCodes,
    getCachedSubCodes,
    isLoading: (comCode) => !!inflightRef.current[comCode],
  };

  return (
    <CommonCodeContext.Provider value={value}>
      {children}
    </CommonCodeContext.Provider>
  );
};

export const useCommonCode = () => {
  const context = useContext(CommonCodeContext);
  if (!context) {
    throw new Error('useCommonCode must be used within a CommonCodeProvider');
  }
  return context;
};
